import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  CheckSquare,
  CircleCheck,
  Clock,
  ListTodo,
  Square,
} from "lucide-react";
import type {
  CheckTasksCompletedResult,
  CreateTasksResult,
  FpMessagePart,
  GetNextTaskResult,
  GetTaskStatsResult,
  GetTasksToolResult,
  UpdateTaskResult,
} from "../../../../../schemas/codegen";
import type { TaskPriority, TaskStatus } from "../../../../../schemas/tasks";

// Type guard for TaskStatus
function isTaskStatus(status: string): status is TaskStatus {
  return ["pending", "in_progress", "completed", "cancelled"].includes(status);
}
import {
  ExpandableMessageCard,
  SimpleMessageCard,
  isBootsmanResult,
} from "./shared";

interface TaskManagementProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

// Task-related result types from central schemas
type TaskManagementResult =
  | CreateTasksResult
  | GetTasksToolResult
  | UpdateTaskResult
  | GetNextTaskResult
  | CheckTasksCompletedResult
  | GetTaskStatsResult;

// Helper function to create status badges
function getStatusBadge(status: TaskStatus) {
  const statusConfig = {
    pending: {
      className:
        "bg-neutral-100 text-neutral-600 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700",
      icon: Square,
    },
    in_progress: {
      className:
        "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800",
      icon: Clock,
    },
    completed: {
      className:
        "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800",
      icon: CircleCheck,
    },
    cancelled: {
      className:
        "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800",
      icon: AlertCircle,
    },
  };

  const config = statusConfig[status];
  if (!config) {
    return null;
  }

  const Icon = config.icon;
  return (
    <Badge
      className={cn(
        "gap-1 text-xs font-medium max-w-32 whitespace-nowrap",
        config.className,
      )}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span className="capitalize truncate">{status.replace("_", " ")}</span>
    </Badge>
  );
}

// Helper function to create priority badges
function getPriorityBadge(priority: TaskPriority) {
  const priorityConfig = {
    low: "bg-neutral-100 text-neutral-600 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400",
    medium:
      "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400",
    high: "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400",
    critical:
      "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400",
  };

  const className = priorityConfig[priority];
  if (!className) {
    return null;
  }

  return (
    <Badge className={cn("text-xs font-medium", className)}>{priority}</Badge>
  );
}

function getTaskIcon(toolName: string) {
  switch (toolName) {
    case "createTasks":
      return ListTodo;
    case "updateTask":
      return CheckSquare;
    case "getTasks":
    case "getTaskStats":
      return ListTodo;
    case "getNextTask":
      return Clock;
    case "checkTasksCompleted":
      return CheckSquare;
    default:
      return Square;
  }
}

function getTaskResult(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
): TaskManagementResult | null {
  if (
    part.status === "complete" &&
    part.result &&
    isBootsmanResult(part.result) &&
    part.result.success
  ) {
    return part.result.data as TaskManagementResult;
  }
  return null;
}

function getTaskTitle(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
): React.ReactNode {
  const { toolName, args } = part;
  const result = getTaskResult(part);

  switch (toolName) {
    case "createTasks": {
      const taskCount = Array.isArray(args?.tasks) ? args.tasks.length : 0;
      return `Creating ${taskCount} task${taskCount !== 1 ? "s" : ""}`;
    }
    case "updateTask": {
      const status = args?.status;

      // Get task title from result (if completed) or args (if provided)
      const updateResult = result as UpdateTaskResult | null;
      const taskTitleFromResult = updateResult?.taskTitle;
      const taskTitleFromArgs = args?.title;
      const taskTitle = taskTitleFromResult || taskTitleFromArgs;

      const taskDisplay = taskTitle ? `"${taskTitle}"` : "task";

      if (status && typeof status === "string" && isTaskStatus(status)) {
        return (
          <div className="flex items-center gap-2">
            <span>Marking {taskDisplay} →</span>
            {getStatusBadge(status)}
          </div>
        );
      }
      return `Updating ${taskDisplay}`;
    }
    case "getTasks": {
      const getTasksResult = result as GetTasksToolResult | null;
      if (getTasksResult?.tasks) {
        const count = Array.isArray(getTasksResult.tasks)
          ? getTasksResult.tasks.length
          : 0;
        return `Retrieved ${count} task${count !== 1 ? "s" : ""}`;
      }
      return "Getting tasks";
    }
    case "getNextTask": {
      const getNextTaskResult = result as GetNextTaskResult | null;
      if (getNextTaskResult?.hasNextTask && getNextTaskResult.task?.title) {
        return `Next: "${getNextTaskResult.task.title}"`;
      }
      if (getNextTaskResult?.hasNextTask === false) {
        return "No pending tasks";
      }
      return "Getting next task";
    }
    case "checkTasksCompleted": {
      const checkTasksResult = result as CheckTasksCompletedResult | null;
      if (checkTasksResult?.allTasksCompleted !== undefined) {
        return checkTasksResult.allTasksCompleted
          ? "All tasks completed"
          : "Tasks remaining";
      }
      return "Checking task completion";
    }
    case "getTaskStats": {
      const getTaskStatsResult = result as GetTaskStatsResult | null;
      if (getTaskStatsResult) {
        const { total = 0, completed = 0 } = getTaskStatsResult;
        return `Task Overview: ${completed}/${total} completed`;
      }
      return "Getting task statistics";
    }
    default:
      return `Task: ${toolName}`;
  }
}

function shouldUseExpandableCard(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
) {
  const { toolName } = part;
  const result = getTaskResult(part);

  // Use expandable for createTasks when we have results
  if (toolName === "createTasks") {
    const createTasksResult = result as CreateTasksResult | null;
    return (createTasksResult?.tasks?.length ?? 0) > 0;
  }

  // Use expandable for getTasks when we have multiple tasks
  if (toolName === "getTasks") {
    const getTasksResult = result as GetTasksToolResult | null;
    return (getTasksResult?.tasks?.length ?? 0) > 0;
  }

  // Use expandable for getTaskStats when we have detailed stats
  if (toolName === "getTaskStats") {
    const getTaskStatsResult = result as GetTaskStatsResult | null;
    return !!getTaskStatsResult;
  }

  // Use expandable for getNextTask when we have task details
  if (toolName === "getNextTask") {
    const getNextTaskResult = result as GetNextTaskResult | null;
    return !!getNextTaskResult?.task;
  }

  return false;
}

function renderExpandableContent(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
) {
  const { toolName } = part;
  const result = getTaskResult(part);

  if (toolName === "createTasks") {
    const createTasksResult = result as CreateTasksResult | null;
    if (createTasksResult?.tasks) {
      const tasks = Array.isArray(createTasksResult.tasks)
        ? createTasksResult.tasks
        : [];
      return (
        <div className="space-y-1">
          {tasks.map((task, index: number) => (
            <div
              key={task.id || index}
              className="flex items-center gap-2 text-sm"
            >
              <Square className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1">{task.title}</span>
              {task.priority &&
                task.priority !== "medium" &&
                getPriorityBadge(task.priority)}
            </div>
          ))}
        </div>
      );
    }
  }

  if (toolName === "getTasks") {
    const getTasksResult = result as GetTasksToolResult | null;
    if (getTasksResult?.tasks) {
      const tasks = Array.isArray(getTasksResult.tasks)
        ? getTasksResult.tasks
        : [];
      const tasksByStatus = tasks.reduce(
        (acc: Record<string, typeof tasks>, task) => {
          if (!acc[task.status]) {
            acc[task.status] = [];
          }
          acc[task.status].push(task);
          return acc;
        },
        {},
      );

      return (
        <div className="space-y-2">
          {Object.entries(tasksByStatus).map(([status, statusTasks]) => (
            <div key={status}>
              <div className="mb-1 flex items-center gap-2">
                {isTaskStatus(status) && getStatusBadge(status)}
                <span className="text-xs text-muted-foreground">
                  ({statusTasks.length})
                </span>
              </div>
              <div className="space-y-0.5 pl-4">
                {statusTasks.map((task) => (
                  <div key={task.id} className="text-sm">
                    {task.title}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
  }

  if (toolName === "getTaskStats") {
    const getTaskStatsResult = result as GetTaskStatsResult | null;
    if (getTaskStatsResult) {
      const {
        completed = 0,
        pending = 0,
        inProgress = 0,
        cancelled = 0,
      } = getTaskStatsResult;
      return (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 gap-1 justify-center">
            <CircleCheck className="w-3 h-3" />
            <span>{completed}</span>
          </Badge>
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 gap-1 justify-center">
            <Clock className="w-3 h-3" />
            <span>{inProgress}</span>
          </Badge>
          <Badge className="bg-neutral-100 text-neutral-600 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 gap-1 justify-center">
            <Square className="w-3 h-3" />
            <span>{pending}</span>
          </Badge>
          {cancelled > 0 && (
            <Badge className="bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 gap-1 justify-center">
              <AlertCircle className="w-3 h-3" />
              <span>{cancelled}</span>
            </Badge>
          )}
        </div>
      );
    }
  }

  if (toolName === "getNextTask") {
    const getNextTaskResult = result as GetNextTaskResult | null;
    if (getNextTaskResult?.task) {
      const task = getNextTaskResult.task;
      return (
        <div className="space-y-1">
          <div className="font-medium text-sm">{task.title}</div>
          {task.description && (
            <div className="text-muted-foreground text-xs">
              {task.description}
            </div>
          )}
          {task.priority && task.priority !== "medium" && (
            <div className="flex items-center gap-2">
              {getPriorityBadge(task.priority)}
            </div>
          )}
        </div>
      );
    }
  }

  return null;
}

export function TaskManagement({ part }: TaskManagementProps) {
  const Icon = getTaskIcon(part.toolName);
  const title = getTaskTitle(part);
  const useExpandable = shouldUseExpandableCard(part);

  if (useExpandable) {
    const expandableContent = renderExpandableContent(part);
    return (
      <ExpandableMessageCard
        status={part.status}
        result={part.result}
        icon={Icon}
        title={title}
        accordionValue={`task-${part.toolName}`}
      >
        {expandableContent}
      </ExpandableMessageCard>
    );
  }

  return <SimpleMessageCard status={part.status} icon={Icon} title={title} />;
}
